import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Shield, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { listShowcase, getMeta } from "@/lib/api";

const TIER_BADGE = (score) => {
    if (score >= 90) return { label: "TIER-S · ELITE", style: "bg-[#FFCC00] text-black border-[#FFCC00]" };
    if (score >= 80) return { label: "TIER-A · STRONG", style: "bg-white text-black border-white" };
    return { label: "ANALYSED", style: "border-white/40 text-white/70" };
};

export default function SolvedCases() {
    const [cases, setCases] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        listShowcase(80)
            .then((r) => setCases(r.cases || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
                        SOLVED · PUBLIC LEDGER · NAMES REDACTED
                    </div>
                    <h1
                        className="font-anton uppercase leading-[0.85] text-5xl sm:text-7xl md:text-8xl"
                        data-testid="solved-headline"
                    >
                        Cases<br />
                        we <span className="text-[#FF3B30]">levelled</span>.
                    </h1>
                    <p className="mt-8 max-w-2xl font-sans text-white/70 text-base sm:text-lg leading-relaxed">
                        Every case here scored ≥80 on HPM's 10-bot analysis. All identifying
                        details are auto-redacted. Strength score, violations, precedents, and
                        recommended actions are public — so future plaintiffs can learn what works.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link
                            to="/hpm-submit"
                            className="btn-brutal btn-brutal-red"
                            data-testid="solved-cta-submit"
                        >
                            ADD YOUR CASE <ArrowRight size={16} />
                        </Link>
                        <Link to="/hpm-search" className="btn-brutal">
                            BROWSE PRECEDENTS
                        </Link>
                    </div>
                </div>
            </section>

            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                        {cases.length} CASE{cases.length === 1 ? "" : "S"} SHOWN · MIN SCORE 80
                    </div>
                    <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-none mb-10">
                        The Wall of Wins.
                    </h2>

                    {loading && (
                        <div className="p-16 text-center">
                            <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                        </div>
                    )}

                    {!loading && cases.length === 0 && (
                        <div className="border-2 border-dashed border-white/20 p-16 text-center">
                            <div className="font-anton text-3xl uppercase">No solved cases yet.</div>
                            <p className="font-sans text-white/60 mt-3">
                                Be the first to reach an 80+ strength score.
                            </p>
                        </div>
                    )}

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-white/20"
                        data-testid="showcase-grid"
                    >
                        {cases.map((c, i) => {
                            const badge = TIER_BADGE(c.strength_score);
                            return (
                                <Link
                                    key={c.id}
                                    to={`/hpm-cases/solved/${c.id}`}
                                    className="block p-6 border-white/20 lg:[&:not(:nth-child(3n))]:border-r-2 md:odd:border-r-2 lg:odd:[&:nth-child(3n)]:border-r-0 [&:not(:last-child)]:border-b-2 hover:bg-white/5 transition-colors"
                                    data-testid={`showcase-card-${i}`}
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                            CASE #{(c.id || "").slice(0, 6).toUpperCase()}
                                        </div>
                                        <div
                                            className={`font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 border-2 ${badge.style}`}
                                        >
                                            {badge.label}
                                        </div>
                                    </div>

                                    <div className="font-anton text-7xl text-[#FF3B30] leading-none mb-1">
                                        {c.strength_score}
                                        <span className="text-white/30 text-xl ml-1">/100</span>
                                    </div>

                                    <div className="font-mono text-[10px] uppercase tracking-wider text-white/60 mt-3">
                                        {(c.case_type || "").replace(/_/g, " ")} · {c.jurisdiction}
                                        {c.state ? ` · ${c.state}` : ""} · {c.language}
                                    </div>

                                    <div className="mt-3 font-sans text-sm text-white/70">
                                        {c.plaintiff_redacted}{" "}
                                        <span className="text-white/30">v.</span> {c.defendant_redacted}
                                    </div>

                                    {c.violations_top3 && c.violations_top3.length > 0 && (
                                        <div className="mt-4">
                                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2 flex items-center gap-2">
                                                <Shield size={10} /> TOP VIOLATIONS
                                            </div>
                                            <ul className="space-y-1">
                                                {c.violations_top3.slice(0, 2).map((v, j) => (
                                                    <li
                                                        key={j}
                                                        className="font-sans text-xs text-white/80 flex gap-2"
                                                    >
                                                        <span className="text-[#FF3B30]">→</span>
                                                        <span className="line-clamp-2">{v}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {c.status === "matched" && (
                                        <div className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] border border-[#FFCC00] px-2 py-1">
                                            <Trophy size={10} /> ATTORNEY MATCHED
                                        </div>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
